/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  VegaFlex,
  VegaFont,
  VegaBox,
  VegaInput,
  VegaForm,
  VegaTabGroupPanel,
  VegaPagination,
  VegaCheckbox,
  VegaButtonCircle
} from '@heartlandone/vega-react';
import MessageDetail from './MessageDetail';
import MessageItem from './MessageItem';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setListCheckBox, initializeMessageState, showMessageDetail } from 'Slices/Header/messageSlice';
import { updateMessages } from 'Chores/request';
import NotFoundMessage from '../../Public/Image/notFoundMessage.svg';
import NoMessage from '../../Public/Image/noMessage.svg';

const DEFAULT_PAGE_SIZE = 10;

const MessageTabGroupPanel = ({ item, localMessageList, setLocalMessageList, messageSource, setMessageSource, selectedTab }) => {
  const dispatch = useDispatch();
  const listCheckBox = useSelector((state) => state.message.listCheckBox);
  const showPagination = useSelector((state) => state.message.showPagination);
  const showMessageItemDetail = useSelector((state) => state.message.showMessageItemDetail);

  const sortType = [
    'far fa-arrow-down-short-wide',
    'far fa-arrow-up-short-wide'
  ];
  const [sortButtonType, setSortButtonType] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const previousSelectedTabRef = useRef(selectedTab);

  const normalizedSearchTerm = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);

  const filteredMessageList = useMemo(() => {
    if (!normalizedSearchTerm) {
      return localMessageList;
    }
    return localMessageList.filter((message) =>
      (message.subject || '').toLowerCase().includes(normalizedSearchTerm)
    );
  }, [localMessageList, normalizedSearchTerm]);

  useEffect(() => {
    dispatch(setListCheckBox(getCurrentListCheckBox(filteredMessageList)));
  }, [filteredMessageList, pagination.page, pagination.pageSize, dispatch]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((filteredMessageList.length || 0) / pagination.pageSize) || 1);
    setPagination((prev) => {
      if (prev.page > totalPages) {
        return { ...prev, page: totalPages };
      }
      if (!filteredMessageList.length && prev.page !== 1) {
        return { ...prev, page: 1 };
      }
      return prev;
    });
  }, [filteredMessageList.length, pagination.pageSize]);

  useEffect(() => {
    if (previousSelectedTabRef.current !== selectedTab) {
      if (item.dataTarget === selectedTab) {
        setPagination({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
        setSearchTerm('');
        dispatch(setListCheckBox(false));
        setLocalMessageList((prevList) => prevList.map((message) => ({ ...message, selected: false })));
      }
      previousSelectedTabRef.current = selectedTab;
    }
  }, [selectedTab, item.dataTarget, dispatch, setLocalMessageList]);

  const getCurrentPageRange = () => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return { startIndex, endIndex };
  };

  const handleSearchChange = (e) => {
    const nextValue = typeof e?.detail === 'string' ? e.detail : '';
    setSearchTerm(nextValue);
    setPagination((prev) => ({ ...prev, page: 1 }));
    dispatch(setListCheckBox(false));
    setLocalMessageList((prevList) => prevList.map((message) => ({ ...message, selected: false })));
  };

  const resolveReadFlag = (message) => {
    if (typeof message?.isRead === 'boolean') {
      return message.isRead;
    }
    return true;
  };

  const computeBackground = (readFlag) => (readFlag ? 'bg-primary' : 'bg-page');

  const buildUpdatePayload = (message, overrides = {}) => {
    const {
      id
    } = message;

    return {
      id,
      ...overrides
    };
  };

  const syncMessageSource = (updatedPayload) => {
    if (!setMessageSource) return;
    setMessageSource((prevSource) => {
      if (!prevSource) return prevSource;
      const nextAll = (prevSource.allLists || []).map((message) =>
        message.id === updatedPayload.id ? { ...message, ...updatedPayload } : message
      );
      return {
        allLists: nextAll,
        unread: nextAll.filter((message) => message.isRead === false),
        system: nextAll.filter((message) => message.messageType === 'system')
      };
    });
  };

  const getCurrentListCheckBox = (sourceList) => {
    const { startIndex, endIndex } = getCurrentPageRange();
    const currentPageItems = sourceList.slice(startIndex, endIndex);
    if (!currentPageItems.length) return false;
    return currentPageItems.every((message) => message.selected);
  };
  const onChangeCheckBox = (e) => {
    const isChecked = e.target.checked;
    const { startIndex, endIndex } = getCurrentPageRange();
    const currentPageItems = filteredMessageList.slice(startIndex, endIndex);
    if (!isChecked && !currentPageItems.every((message) => message.selected)) {
      return;
    }
    const currentPageIds = new Set(currentPageItems.map((message) => message.id));
    setLocalMessageList((prevList) =>
      prevList.map((message) => {
        if (currentPageIds.has(message.id)) {
          return {
            ...message,
            selected: isChecked
          };
        }
        return message;
      })
    );
    dispatch(setListCheckBox((isChecked)));
  };
  const changSortType = () => {
    setSortButtonType((prevType) => {
      const nextType = prevType ? 0 : 1;
      const sortAscending = nextType === 1;

      const sortedList = [...localMessageList].sort((a, b) => {
        const timeA = new Date(a.creationTime || 0).getTime();
        const timeB = new Date(b.creationTime || 0).getTime();
        if (sortAscending) {
          return timeA - timeB;
        }
        return timeB - timeA;
      });

      setLocalMessageList(sortedList);
      return nextType;
    });
  };

  const handlePageChange = (e) => {
    const newPage = e.detail;
    setPagination((prev) => ({ ...prev, page: newPage }));
    dispatch(setListCheckBox(false));
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = e.detail;
    setPagination((prev) => ({ ...prev, page: 1, pageSize: newPageSize }));
    dispatch(setListCheckBox(false));
    setLocalMessageList((prevList) => prevList.map((message) => ({ ...message, selected: false })));
  };

  const paginatedMessages = useMemo(() => {
    const { startIndex, endIndex } = getCurrentPageRange();
    return filteredMessageList.slice(startIndex, endIndex);
  }, [filteredMessageList, pagination.page, pagination.pageSize]);

  const batchDeleteMessages = async () => {
    const selectedMessages = localMessageList.filter((message) => message.selected);
    if (!selectedMessages.length) {
      return;
    }

    dispatch(initializeMessageState());

    const selectedIds = new Set(selectedMessages.map((message) => message.id));
    setLocalMessageList((prevList) => prevList.filter((message) => !selectedIds.has(message.id)));
    dispatch(setListCheckBox(false));

    if (setMessageSource) {
      setMessageSource((prevSource) => {
        if (!prevSource) return prevSource;
        const nextAll = (prevSource.allLists || []).filter((message) => !selectedIds.has(message.id));
        return {
          allLists: nextAll,
          unread: nextAll.filter((message) => message.isRead === false),
          system: nextAll.filter((message) => message.messageType === 'system')
        };
      });
    }

    const deletePayloads = selectedMessages
      .filter((message) => message?.id)
      .map((message) => buildUpdatePayload(message, { isDeleted: true, isRead: true }));

    if (deletePayloads.length) {
      try {
        await updateMessages(deletePayloads);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to batch delete messages', error);
      }
    }

    // Clear any lingering selection after the API call completes
    setLocalMessageList((prevList) => prevList.map((message) => ({ ...message, selected: false })));
  };

  const batchMarkAsUnread = async () => {
    const selectedMessages = localMessageList.filter((message) => message.selected && message.isRead !== false);
    if (!selectedMessages.length) {
      return;
    }

    const selectedIds = new Set(selectedMessages.map((message) => message.id));

    setLocalMessageList((prevList) =>
      prevList.map((message) => {
        if (selectedIds.has(message.id)) {
          return {
            ...message,
            isRead: false,
            trigger: false,
            backgroundColor: computeBackground(false),
            selected: false
          };
        }
        return { ...message, selected: false };
      })
    );
    dispatch(setListCheckBox(false));

    if (setMessageSource) {
      setMessageSource((prevSource) => {
        if (!prevSource) return prevSource;
        const nextAll = (prevSource.allLists || []).map((message) =>
          selectedIds.has(message.id) ? { ...message, isRead: false } : message
        );
        return {
          allLists: nextAll,
          unread: nextAll.filter((message) => message.isRead === false),
          system: nextAll.filter((message) => message.messageType === 'system')
        };
      });
    }

    const unreadPayloads = selectedMessages
      .filter((message) => message?.id)
      .map((message) => buildUpdatePayload(message, { isRead: false }));

    if (unreadPayloads.length) {
      try {
        await updateMessages(unreadPayloads);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to batch mark messages as unread', error);
      }
    }

    // Ensure checkboxes are cleared after the operation
    setLocalMessageList((prevList) => prevList.map((message) => ({ ...message, selected: false })));
  };

  const batchMarkAsRead = async () => {
    const selectedMessages = localMessageList.filter((message) => message.selected && message.isRead === false);
    if (!selectedMessages.length) {
      return;
    }

    const selectedIds = new Set(selectedMessages.map((message) => message.id));

    setLocalMessageList((prevList) =>
      prevList.map((message) => {
        if (selectedIds.has(message.id)) {
          return {
            ...message,
            isRead: true,
            trigger: false,
            backgroundColor: computeBackground(true),
            selected: false
          };
        }
        return { ...message, selected: false };
      })
    );
    dispatch(setListCheckBox(false));

    if (setMessageSource) {
      setMessageSource((prevSource) => {
        if (!prevSource) return prevSource;
        const nextAll = (prevSource.allLists || []).map((message) =>
          selectedIds.has(message.id) ? { ...message, isRead: true } : message
        );
        return {
          allLists: nextAll,
          unread: nextAll.filter((message) => message.isRead === false),
          system: nextAll.filter((message) => message.messageType === 'system')
        };
      });
    }

    const readPayloads = selectedMessages
      .filter((message) => message?.id)
      .map((message) => buildUpdatePayload(message, { isRead: true }));

    if (readPayloads.length) {
      try {
        await updateMessages(readPayloads);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to batch mark messages as read', error);
      }
    }

    // Ensure checkboxes are cleared after the operation
    setLocalMessageList((prevList) => prevList.map((message) => ({ ...message, selected: false })));
  };

  const deleteMessage = async (messageItem) => {
    dispatch(initializeMessageState());
    setLocalMessageList((prevList) => prevList.filter((message) => message.id !== messageItem.id));
    if (setMessageSource) {
      setMessageSource((prevSource) => {
        if (!prevSource) return prevSource;
        const nextAll = (prevSource.allLists || []).filter((message) => message.id !== messageItem.id);
        return {
          allLists: nextAll,
          unread: nextAll.filter((message) => message.isRead === false),
          system: nextAll.filter((message) => message.messageType === 'system')
        };
      });
    }

    if (messageItem?.id) {
      const deletePayload = buildUpdatePayload(messageItem, { isDeleted: true });
      try {
        await updateMessages([deletePayload]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete message', error);
      }
    }
  };
  const triggeredMessage = async (messageItem) => {
    const shouldUpdate = messageItem?.isRead === false;
    const overrides = shouldUpdate ? { isRead: true } : {};
    const updatePayload = buildUpdatePayload(messageItem, overrides);
    const detailPayload = { ...messageItem, ...overrides };
    dispatch(showMessageDetail(detailPayload));

    setLocalMessageList((prevList) =>
      prevList.map((message) => {
        if (message.id === messageItem.id) {
          return {
            ...message,
            ...updatePayload,
            isRead: resolveReadFlag(updatePayload),
            trigger: true,
            backgroundColor: 'bg-table-selected-row'
          };
        }
        const readFlag = resolveReadFlag(message);
        return {
          ...message,
          trigger: false,
          backgroundColor: computeBackground(readFlag)
        };
      })
    );

    if (shouldUpdate) {
      syncMessageSource(updatePayload);

      try {
        await updateMessages([updatePayload]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update message read status', error);
      }
    }
  };
  const unTriggeredMessage = (messageItem) => {
    dispatch(initializeMessageState());
    setLocalMessageList((prevList) =>
      prevList.map((message) => {
        if (message.id === messageItem.id) {
          const readFlag = resolveReadFlag(message);
          return {
            ...message,
            trigger: false,
            backgroundColor: computeBackground(readFlag)
          };
        }
        return message;
      })
    );
  };

  return (
    <>
      <VegaTabGroupPanel id={item.dataTarget} dataVegaTabPanel={item.dataTarget}>
        <VegaFlex
          direction="row"
          alignItems="stretch"
          justifyContent="start"
          gap="0"
          flexGrow="initial"
        >
          <VegaBox padding="size-24" backgroundColor="bg-primary" flexGrow={1} flexBasis={0}>
            <VegaForm id="all-messages-form">
              <VegaFlex
                direction="col"
                alignItems="stretch"
                justifyContent="start"
                gap="size-16"
              >
                <VegaFlex
                  direction="col"
                  alignItems="stretch"
                  justifyContent="center"
                  gap="size-16"
                >
                  <VegaFont
                      color="text-sidebar-link-hover"
                      variant="font-h5"
                      style={{ display: 'block', verticalAlign: 'top', alignSelf: 'stretch' }}
                      textAlign="left"
                    >
                      {item.label}
                  </VegaFont>

                  <VegaInput
                    id="message-search-input"
                    dataVegaForm="input"
                    prefixIcon="far fa-search"
                    showClearIcon={false}
                    size='small'
                    value={searchTerm}
                    onVegaChange={handleSearchChange}
                  >
                  </VegaInput>
                </VegaFlex>

                <VegaFlex
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap="size-16"
                >
                  <VegaFlex
                    direction="row"
                    alignItems="center"
                    justifyContent="start"
                    gap="size-16"
                    style={{ marginLeft: '12px' }}
                  >
                    <VegaCheckbox
                        size="small"
                        disabled="false"
                        checked={listCheckBox}
                        onVegaChange={onChangeCheckBox}
                      >
                        Select All
                    </VegaCheckbox>
                    {localMessageList.some((message) => message.selected) ?
                      <VegaFlex
                        direction="row"
                        alignItems="center"
                        justifyContent="start"
                      >
                        <VegaButtonCircle
                          size="default"
                          disabled="false"
                          danger="false"
                          variant="icon-only"
                          icon="far fa-envelope"
                          iconColor="text-sidebar-link-hover"
                          id="Batch-mark-as-unread-message-button"
                          onVegaClick={batchMarkAsUnread}
                        >
                        </VegaButtonCircle>
                        <VegaButtonCircle
                          size="default"
                          disabled="false"
                          danger="false"
                          variant="icon-only"
                          icon="far fa-envelope-open"
                          iconColor="text-sidebar-link-hover"
                          id="Batch-mark-as-read-message-button"
                          onVegaClick={batchMarkAsRead}
                        >
                        </VegaButtonCircle>
                        <VegaButtonCircle
                          size="default"
                          disabled="false"
                          danger="false"
                          variant="icon-only"
                          icon="far fa-trash-can"
                          iconColor="text-sidebar-link-hover"
                          id="Batch-delete-message-button"
                          onVegaClick={batchDeleteMessages}
                        >
                        </VegaButtonCircle>
                      </VegaFlex> :
                    ''}
                  </VegaFlex>

                  <VegaButtonCircle
                      size="default"
                      disabled="false"
                      danger="false"
                      variant="icon-only"
                      icon={sortType[sortButtonType ? 1 : 0]}
                      onVegaClick={changSortType}
                      iconColor="text-sidebar-link-hover"
                    >
                  </VegaButtonCircle>
                </VegaFlex>

                {paginatedMessages.length > 0 ? paginatedMessages.map((messageItem) =>
                  <MessageItem
                    key={messageItem.id}
                    messageItem={messageItem}
                    setLocalMessageList={setLocalMessageList}
                    triggeredMessage={triggeredMessage}
                    unTriggeredMessage={unTriggeredMessage}
                  />):
                <VegaFlex
                  dataFigmaNodeName='Empty Page'
                  direction='col'
                  alignItems='center'
                  justifyContent='center'
                  gap='0'
                >
                  <img
                    style={{ height: '200px', width: '200px' }}
                    src={searchTerm ? NotFoundMessage : NoMessage}
                  />

                  <VegaBox
                    dataFigmaNodeName='Title'
                    padding={{ top: 'size-32', right: '0', bottom: '0', left: '0' }}
                    corners='rounded-none'
                  >
                    <VegaFont
                      dataFigmaNodeName='Title'
                      color='text-primary'
                      variant='font-h4'
                      style={{
                        display: 'block',
                        verticalAlign: 'top',
                        alignSelf: 'stretch'
                      }}
                      textAlign='center'
                    >
                      {searchTerm ? 'No Results Found' : 'No Messages Found'}
                    </VegaFont>
                  </VegaBox>
                </VegaFlex>}
            {
              showPagination && paginatedMessages.length > 0 ?
              <VegaPagination
                current={pagination.page}
                page-size={pagination.pageSize}
                total={localMessageList.length}
                extensions={[
                  ['currentPageLabel', 'pageSizeSelector'],
                  ['pageSelector', 'goTo']
                ]}
                onVegaChange={handlePageChange}
                onVegaPageSizeChange={handlePageSizeChange}
              >
              </VegaPagination> : ''}
              </VegaFlex>
            </VegaForm>
          </VegaBox>
          {
            showMessageItemDetail ?
            <MessageDetail
              unTriggeredMessage={unTriggeredMessage}
              deleteMessage={deleteMessage}
            /> : ''}

        </VegaFlex>
      </VegaTabGroupPanel>
    </>
  );
};

MessageTabGroupPanel.propTypes = {
  item: PropTypes.object,
  localMessageList: PropTypes.array,
  setLocalMessageList: PropTypes.func,
  messageSource: PropTypes.object,
  setMessageSource: PropTypes.func,
  selectedTab: PropTypes.string
};

export default MessageTabGroupPanel;
